import { chatAuthHeaders, getResolvedApiSuiteBase } from "@/apiBase";
import { chatSuiteApiUrl, chatSuiteFetch } from "@/lib/suite/chatSuiteFetch";
import { readStoredSuiteUserId } from "@/suiteSession";
import { fetchCoffreUnlocked } from "@/lib/suite/saveChatAttachment";

export type ChatCoffreZone = "vault" | "ged";

export type ChatSuiteEntry = {
  name: string;
  path: string;
  kind: "folder" | "file";
  size?: number;
};

function authHeaders(): Record<string, string> {
  return chatAuthHeaders(readStoredSuiteUserId());
}

export { fetchCoffreUnlocked };

export async function fetchCoffreAttachList(
  path = "",
  zone: ChatCoffreZone = "vault"
): Promise<{ path: string; entries: ChatSuiteEntry[]; error?: string }> {
  const qs = new URLSearchParams();
  if (path) qs.set("path", path);
  qs.set("zone", zone);
  const res = await chatSuiteFetch<{
    path?: string;
    entries?: Array<{ name: string; path: string; kind?: string; size?: number | null }>;
    message?: string;
    error?: string;
  }>(`coffre-list.php?${qs.toString()}`);
  if (!res.ok) {
    return {
      path,
      entries: [],
      error: res.data.message ?? res.data.error ?? "Impossible de lire le Coffre.",
    };
  }
  const entries = (res.data.entries ?? [])
    .filter((e) => e.kind === "folder" || e.kind === "file")
    .map((e) => ({
      name: e.name,
      path: e.path,
      kind: e.kind === "folder" ? "folder" as const : "file" as const,
      size: typeof e.size === "number" ? e.size : undefined,
    }));
  return { path: res.data.path ?? path, entries };
}

export async function fetchCoffreAttachFile(
  path: string,
  zone: ChatCoffreZone,
  fallbackName: string
): Promise<{ file: File | null; error?: string }> {
  const qs = new URLSearchParams();
  qs.set("path", path);
  qs.set("zone", zone);
  qs.set("stream", "1");
  const url = chatSuiteApiUrl(`coffre-download.php?${qs.toString()}`);
  if (!url) return { file: null, error: "API Suite introuvable." };
  try {
    const res = await fetch(url, { credentials: "include", headers: authHeaders() });
    if (!res.ok) {
      let message = `Erreur HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { message?: string };
        if (body.message) message = body.message;
      } catch {
        /* binaire */
      }
      return { file: null, error: message };
    }
    const blob = await res.blob();
    if (blob.size <= 0) {
      return { file: null, error: "Fichier Coffre vide. Réessayez ou joignez-le depuis l’appareil." };
    }
    const name = fallbackName || path.split("/").pop() || "fichier";
    return { file: new File([blob], name, { type: blob.type || "application/octet-stream" }) };
  } catch (err) {
    return {
      file: null,
      error: err instanceof Error ? err.message : "Téléchargement Coffre impossible.",
    };
  }
}

export async function fetchPhotosAttachList(
  path = ""
): Promise<{ path: string; folders: ChatSuiteEntry[]; files: ChatSuiteEntry[]; error?: string }> {
  const qs = new URLSearchParams();
  qs.set("view", "folder");
  if (path) qs.set("path", path);
  const res = await chatSuiteFetch<{
    path?: string;
    folders?: Array<{ name: string; path: string }>;
    files?: Array<{ name: string; path: string; size?: number }>;
    message?: string;
  }>(`photos-list.php?${qs.toString()}`);
  if (!res.ok) {
    return {
      path,
      folders: [],
      files: [],
      error: res.data.message ?? "Impossible de lire Photos.",
    };
  }
  return {
    path: res.data.path ?? path,
    folders: (res.data.folders ?? []).map((f) => ({ ...f, kind: "folder" as const })),
    files: (res.data.files ?? []).map((f) => ({
      name: f.name,
      path: f.path,
      kind: "file" as const,
      size: f.size,
    })),
  };
}

export function photosAttachThumbUrl(path: string): string | null {
  const base = getResolvedApiSuiteBase();
  if (!base) return null;
  const qs = new URLSearchParams({ path, size: "thumb" });
  return `${base.replace(/\/$/, "")}/photos-file.php?${qs.toString()}`;
}

export async function fetchPhotosAttachFile(
  path: string,
  fallbackName: string
): Promise<{ file: File | null; error?: string }> {
  const qs = new URLSearchParams({ path, size: "full" });
  const url = chatSuiteApiUrl(`photos-file.php?${qs.toString()}`);
  if (!url) return { file: null, error: "API Suite introuvable." };
  try {
    const res = await fetch(url, { credentials: "include", headers: authHeaders() });
    if (!res.ok) return { file: null, error: `Erreur HTTP ${res.status}` };
    const blob = await res.blob();
    if (blob.size <= 0) return { file: null, error: "Photo vide." };
    const name = fallbackName || path.split("/").pop() || "photo.jpg";
    return { file: new File([blob], name, { type: blob.type || "image/jpeg" }) };
  } catch (err) {
    return {
      file: null,
      error: err instanceof Error ? err.message : "Chargement photo impossible.",
    };
  }
}

export function formatAttachSize(n?: number): string {
  const v = Number(n) || 0;
  if (v < 1) return "";
  if (v < 1024) return `${Math.round(v)} o`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(v >= 10 * 1024 ? 0 : 1)} Ko`;
  return `${(v / (1024 * 1024)).toFixed(1)} Mo`;
}
